import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import ProductCatalogView from '../components/catalog/ProductCatalogView';
import { Search } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

/**
 * SearchPage V3 — Recherche + catalogue complet sans critère (Phase 3.4.3)
 * Route: /search ou /search?q=query
 */
const SearchPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';
  const [searchInput, setSearchInput] = useState(query);

  // Sync input when URL query changes (browser back/forward)
  useEffect(() => {
    setSearchInput(query);
  }, [query]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = searchInput.trim();
    if (trimmed) {
      navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    } else {
      // Empty search → show full catalog
      navigate('/search');
    }
  };

  const contextFilters = useMemo(() => {
    const f = {};
    if (query) f.search = query;
    return f;
  }, [query]);

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="search-page">
        <div>
          <h1 className="text-2xl font-heading font-bold text-gray-900">
            {query ? 'Recherche' : 'Catalogue'}
          </h1>
          {query && (
            <p className="text-sm text-gray-500 mt-1">
              Résultats pour "<span className="font-medium text-gray-900">{query}</span>"
            </p>
          )}
          {!query && (
            <p className="text-sm text-gray-500 mt-1">
              Parcourez l'ensemble du catalogue et affinez avec les filtres
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="flex gap-2 max-w-xl">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="search"
              placeholder="Rechercher un produit, un éditeur, un EAN..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-10"
              data-testid="search-page-input"
            />
          </div>
          <Button type="submit" data-testid="search-submit-button">
            Rechercher
          </Button>
        </form>

        {/* Always show catalog — with or without search query */}
        <ProductCatalogView
          contextFilters={contextFilters}
          syncURL={true}
          emptyMessage={query
            ? "Aucun résultat. Essayez avec d'autres mots-clés ou ajustez les filtres."
            : "Aucun produit disponible."
          }
        />
      </div>
    </DashboardLayout>
  );
};

export default SearchPage;
